import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { 
  Gift, Plus, Check, DollarSign, Star, X, Calendar, User, Building,
  History as HistoryIcon // ✅ CORRECCIÓN 1: Renombramos para evitar crash
} from 'lucide-react'; 
import type { Reward, RewardFormData } from '../types/reward';
import RewardForm from '../components/RewardForm';
import RewardsList from '../components/RewardsList';
import OtherBranchRewardsToggle from '../components/OtherBranchRewardsToggle';
import { useError } from '../../../context/ErrorContext';
import { rewardsApi } from '../../../api/rewardsApi';
import BrandLoader from '../../../components/common/BrandLoader';

// Interface adaptada a la respuesta de Elasticsearch
interface RewardRedemptionHistory {
  id: string;
  visitorName: string;
  visitorPhone: string;
  redeemedAt: string; // Elastic devuelve fecha como string
  points: number;
  branchName?: string;
  userName?: string;
}

const RewardsManagementPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { handleFirebaseError, clearErrors } = useError();
  
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const [saving, setSaving] = useState(false);

  // Estados del Historial
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedRewardHistory, setSelectedRewardHistory] = useState<Reward | null>(null);
  const [redemptionHistory, setRedemptionHistory] = useState<RewardRedemptionHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  // 1. Cargar Recompensas (Firebase)
  const fetchRewards = useCallback(async () => {
    if (!currentUser?.businessId) return;
    try {
      setLoading(true);
      clearErrors();
      const rewardsList = await rewardsApi.list();
      setRewards(rewardsList);
    } catch (error) {
      console.error('Error al obtener recompensas:', error);
      handleFirebaseError(error, 'fetchRewards');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.businessId, clearErrors, handleFirebaseError]);

  useEffect(() => { fetchRewards(); }, [fetchRewards]);

  // ✅ 2. Cargar Historial (Elasticsearch)
  const handleViewHistory = async (reward: Reward) => {
      if (!currentUser?.businessId) return;
      
      setSelectedRewardHistory(reward);
      setShowHistoryModal(true);
      setLoadingHistory(true);
      setRedemptionHistory([]); 

      try {
          const result = await rewardsApi.redemptions(reward.id, '', 1, 50);
          setRedemptionHistory(result.items);
      } catch (error) {
          console.error("Error cargando historial de canjes:", error);
      } finally {
          setLoadingHistory(false);
      }
  };

  // 3. Guardar (Firebase)
  const handleSubmit = async (formData: RewardFormData) => {
    if (!currentUser?.businessId) return;
    try {
      setSaving(true);
      clearErrors();
      const finalCost = loyaltyMode === 'cashback' ? parseFloat(formData.points.toFixed(2)) : Math.floor(formData.points);
      const payload: RewardFormData = { ...formData, points: finalCost };

      if (editingReward) {
        await rewardsApi.update(editingReward.id, payload);
        setSuccess('Recompensa actualizada correctamente');
      } else {
        await rewardsApi.create(payload);
        setSuccess('Recompensa creada correctamente');
      }
      setShowForm(false);
      setEditingReward(null);
      fetchRewards();
      setTimeout(() => { setSuccess(null); }, 3000);
    } catch (error) {
      console.error('Error al guardar recompensa:', error);
      handleFirebaseError(error, 'saveReward');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (reward: Reward) => { clearErrors(); setEditingReward(reward); setShowForm(true); };

  // 4. Eliminar (Firebase)
  const handleDelete = async (rewardId: string) => {
    try {
      clearErrors();
      await rewardsApi.remove(rewardId);
      setSuccess('Recompensa eliminada correctamente');
      fetchRewards();
      setTimeout(() => { setSuccess(null); }, 3000);
    } catch (error) {
      console.error('Error al eliminar recompensa:', error);
      handleFirebaseError(error, 'deleteReward');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <Gift className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" />
              Gestión de Recompensas
            </h1>
            <div className="mt-1 flex items-center text-sm text-content-2">
              <span>Administra el catálogo de premios</span>
              <span className="mx-2">•</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${loyaltyMode === 'cashback' ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300'}`}>
                {loyaltyMode === 'cashback' ? <><DollarSign className="w-3 h-3 mr-1" /> Modo Cashback</> : <><Star className="w-3 h-3 mr-1" /> Modo Puntos</>}
              </span>
            </div>
          </div>
          {!showForm && (
            <button onClick={() => { clearErrors(); setShowForm(true); }} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent w-full sm:w-auto animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <Plus className="h-4 w-4 mr-2" /> Nueva Recompensa
            </button>
          )}
        </div>

        {/* Recompensas por sucursal (10-oct-2026): solo con 2+ sucursales y solo dueño/gerente */}
        {!showForm && (currentUser?.branches || []).filter((b) => b.isActive !== false).length >= 2 && currentUser?.baseRole !== 'staff' && (
          <OtherBranchRewardsToggle />
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex"><div className="flex-shrink-0"><Check className="h-5 w-5 text-green-400" /></div><div className="ml-3"><p className="text-sm text-green-700 dark:text-green-300">{success}</p></div></div>
          </div>
        )}

        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:p-6">
            {showForm ? (
              <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                <h2 className="text-lg font-medium text-content mb-4">{editingReward ? 'Editar Recompensa' : 'Nueva Recompensa'}</h2>
                <RewardForm initialData={editingReward || undefined} onSubmit={handleSubmit} onCancel={() => { setShowForm(false); setEditingReward(null); clearErrors(); }} loading={saving} />
              </div>
            ) : (
              <RewardsList 
                rewards={rewards} 
                onEdit={(reward) => { clearErrors(); handleEdit(reward); }} 
                onDelete={handleDelete} 
                loading={loading} 
                onViewHistory={handleViewHistory} // ✅ Usamos la función optimizada
              />
            )}
          </div>
        </div>
      </div>

      {/* ✅ MODAL DE HISTORIAL */}
      {showHistoryModal && selectedRewardHistory && (
          <div onClick={() => setShowHistoryModal(false)} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm modal-overlay">
              <div onClick={(e) => e.stopPropagation()} className="bg-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden modal-panel">
                  
                  <div className="px-6 py-4 border-b border-line flex justify-between items-center bg-surface-2">
                      <div>
                          <h3 className="text-lg font-bold text-content flex items-center">
                              {/* ✅ Usamos HistoryIcon */}
                              <HistoryIcon className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
                              Historial de Canjes
                          </h3>
                          <p className="text-sm text-content-3 mt-1">
                              {selectedRewardHistory.title}
                          </p>
                      </div>
                      <button onClick={() => setShowHistoryModal(false)} className="text-content-3 hover:text-content-2 transition-colors">
                          <X className="w-6 h-6" />
                      </button>
                  </div>

                  <div className="flex-grow overflow-y-auto p-6">
                      {loadingHistory ? (
                          <div className="text-center py-12">
                              <BrandLoader size={36} />
                              <p className="text-content-3">Cargando historial...</p>
                          </div>
                      ) : redemptionHistory.length === 0 ? (
                          <div className="text-center py-12 flex flex-col items-center">
                              <div className="bg-canvas p-4 rounded-full mb-3"><HistoryIcon className="w-8 h-8 text-content-3" /></div>
                              <p className="text-content-2 font-medium">Nadie ha canjeado esta recompensa aún.</p>
                              <p className="text-sm text-content-3 mt-1">¡Promociónala más con tus clientes!</p>
                          </div>
                      ) : (
                          <div className="space-y-3">
                              {redemptionHistory.map((redemption) => (
                                  <div key={redemption.id} className="flex items-center justify-between p-3 border border-line rounded-lg hover:bg-surface-2 transition-colors group">
                                      
                                      <div className="flex items-center gap-3">
                                          <div className="bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 p-2 rounded-full group-hover:bg-purple-100 dark:group-hover:bg-purple-500/20 transition-colors">
                                              <User className="w-4 h-4" />
                                          </div>
                                          <div>
                                              <p className="text-sm font-medium text-content">{redemption.visitorName || 'Cliente'}</p>
                                              <p className="text-xs text-content-3">{redemption.visitorPhone}</p>
                                          </div>
                                      </div>

                                      <div className="text-right">
                                          <div className="flex items-center justify-end text-sm font-semibold text-content-2">
                                              {loyaltyMode === 'cashback' ? <DollarSign className="w-3 h-3 mr-1 text-green-600 dark:text-green-400" /> : <Star className="w-3 h-3 mr-1 text-yellow-500" />}
                                              {loyaltyMode === 'cashback' ? redemption.points.toFixed(2) : Math.floor(redemption.points)}
                                          </div>
                                          <div className="flex items-center justify-end text-xs text-content-3 mt-0.5">
                                              <Calendar className="w-3 h-3 mr-1" />
                                              {/* Elastic devuelve fecha ISO string, la convertimos */}
                                              {new Date(redemption.redeemedAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                                          </div>
                                          {redemption.branchName && (
                                               <div className="flex items-center justify-end text-[10px] text-content-3 mt-0.5">
                                                  <Building className="w-2.5 h-2.5 mr-1" />
                                                  {redemption.branchName}
                                               </div>
                                          )}
                                          {redemption.userName && (
                                               <div className="text-right text-[10px] text-content-3 mt-0.5">Atendió {redemption.userName}</div>
                                          )}
                                      </div>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
                  
                  <div className="px-6 py-4 border-t border-line bg-surface-2 flex justify-end">
                      <button onClick={() => setShowHistoryModal(false)} className="px-4 py-2 bg-surface border border-line rounded-md text-sm font-medium text-content-2 hover:bg-canvas transition-colors">
                          Cerrar
                      </button>
                  </div>
              </div>
          </div>
      )}
    </Layout>
  );
};

export default RewardsManagementPage;